import React, { useEffect, useRef, useState } from "react";
import { TextInput } from "@narmi/design_system";
import Utils from "byzantine/src/utils";

const getMaxLengthFromCardNumber = (cardNumber: string) => {
  // for our purposes, only an amex has a 4-digit cvc
  if (Utils.isPossiblyAnAmexCardNumber(cardNumber)) {
    return 4;
  }
  return 3;
};

export const validateCvc = (
  value: string,
  requiredError = "Required",
  invalidError = "Invalid CVC"
): string | null => {
  if (!value) {
    return requiredError;
  }
  if (value.length < 3) {
    return invalidError;
  }
  return null;
};

type CardCvcTextInputProps = {
  onChange?: (value: string) => void;
  value?: string;
  field?: string;
  cardNumber?: string;
  supportsFourDigitCvc?: boolean;
} & React.ComponentProps<typeof TextInput>;

const CardCvcTextInput = ({
  onChange,
  value,
  field,
  cardNumber,
  supportsFourDigitCvc,
  ...textInputProps
}: CardCvcTextInputProps) => {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  const [needCursorUpdate, setNeedCursorUpdate] = useState(false);
  // if the cardNumber changes, we should wipe the CVC code in the event that the original pin is too long
  useEffect(() => {
    const maxCvcLength = getMaxLengthFromCardNumber(cardNumber ?? "");
    let parsedValue = (value ?? "").replace(/[^\d]/g, "");
    if (parsedValue.length > maxCvcLength) {
      parsedValue = "";
    }
    onChange?.(parsedValue);
  }, [cardNumber]);

  const handleInput = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const maxCvcLength = supportsFourDigitCvc ? getMaxLengthFromCardNumber(cardNumber ?? "") : 3;
    const parsedValue = event.target.value.substring(0, maxCvcLength).replace(/[^\d]/g, "");
    onChange?.(parsedValue);

    const currentCursorPosition = inputRef.current?.selectionStart;
    const isCursorInMiddleOfString =
      currentCursorPosition != null && currentCursorPosition < event.target.value.length;
    if (isCursorInMiddleOfString) {
      setCursorPosition(currentCursorPosition);
      setNeedCursorUpdate(true);
    }
  };

  useEffect(() => {
    if (needCursorUpdate) {
      inputRef.current?.setSelectionRange(cursorPosition, cursorPosition);
      setNeedCursorUpdate(false);
    }
  }, [needCursorUpdate]);

  return (
    <TextInput
      onChange={handleInput}
      value={value}
      field={field}
      inputMode="numeric"
      autoComplete="cc-csc"
      {...textInputProps}
      ref={inputRef}
    />
  );
};

export default CardCvcTextInput;
